import type { Freshness, ShippedResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { IncompleteMark } from '@/components/pulse/freshness';
import { ArrowIcon, BackIcon, CalendarIcon } from '@/components/pulse/icons';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useShell } from '@/domains/shell/api';
import { useShipped } from '@/domains/shipped/api';

type Project = ShippedResponse['projects'][number];
type Narrative = ShippedResponse['team'];
type Item = Project['delivered'][number];

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function shippedSearch(view: 'week' | 'month', period: string | null): string {
  const parts = [view === 'month' ? 'view=month' : '', period === null ? '' : `period=${period}`].filter(Boolean);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/** SHP-P1: Week | Month, and a stepper that stops at the current period. */
export function ShippedControl({ data, onChange }: { readonly data: ShippedResponse; readonly onChange: (view: 'week' | 'month', period: string | null) => void }): React.ReactElement {
  const noun = data.view === 'week' ? 'week' : 'month';
  return (
    <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
      <div className="seg" role="group" aria-label="Period">
        <button type="button" aria-pressed={data.view === 'week'} onClick={() => onChange('week', null)}>
          Week
        </button>
        <button type="button" aria-pressed={data.view === 'month'} onClick={() => onChange('month', null)}>
          Month
        </button>
      </div>
      <div className="row" style={{ gap: 6 }} role="group" aria-label={data.view === 'week' ? 'Week' : 'Month'}>
        <button type="button" className="btn" aria-label={`Earlier ${noun}`} onClick={() => onChange(data.view, data.previous)}>
          <BackIcon />
        </button>
        <span className="datepill">
          <CalendarIcon />
          <span>{data.label.charAt(0).toUpperCase() + data.label.slice(1)}</span>
        </span>
        <button
          type="button"
          className="btn"
          aria-label={`Later ${noun}`}
          disabled={data.next === null}
          title={data.next === null ? `This is the current ${noun}` : undefined}
          onClick={() => onChange(data.view, data.next)}
        >
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
            <BackIcon />
          </span>
        </button>
      </div>
    </div>
  );
}

/** Shipped (SHP-P1 to SHP-P6), built from the prototype's `viewShipped()`. */
export function ShippedPage(): React.ReactElement {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const view = search.get('view') === 'month' ? 'month' : 'week';
  const page = useShipped(view, search.get('period'));
  const shell = useShell();
  const data = page.data;
  const go = (nextView: 'week' | 'month', nextPeriod: string | null) => navigate({ search: shippedSearch(nextView, nextPeriod) });
  return (
    <>
      <PageHeader
        eyebrow={`Shipped · ${data?.label ?? ''}`}
        title="What shipped"
        sub="What each project delivered to production, grouped by the story or task it was for, and what is on dev. Production means a PR merged into main."
        right={data !== undefined ? <ShippedControl data={data} onChange={go} /> : undefined}
      />
      {data !== undefined ? <ShippedView data={data} freshness={shell.data?.freshness ?? null} /> : null}
    </>
  );
}

/** SHP-P6: Talos's narrative, or Pulse's summary until Talos writes it. */
function NarrativeText({ narrative }: { readonly narrative: Narrative }): React.ReactElement {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: narrative.by === 'talos' ? 'var(--ink)' : 'var(--ink-2)' }}>{narrative.text}</p>
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{narrative.caption}</span>
    </div>
  );
}

/** SHP-P3: a long list shows its first five, then Show N more. */
export function FirstFive({ rows, label }: { readonly rows: ReadonlyArray<React.ReactElement>; readonly label: string }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      {open ? rows : rows.slice(0, 5)}
      {rows.length > 5 ? (
        <button type="button" className="linkish" aria-expanded={open} aria-label={open ? `Show fewer ${label}` : `Show ${rows.length - 5} more ${label}`} onClick={() => setOpen(!open)} style={{ alignSelf: 'flex-start', padding: '8px 0', background: 'none', border: 0, cursor: 'pointer' }}>
          {open ? 'Show fewer' : `Show ${rows.length - 5} more`}
        </button>
      ) : null}
    </>
  );
}

/** SHP-P8: a release into production, or a PR merged straight into it, opening its view. */
function ReleaseRow({ release }: { readonly release: Project['releases'][number] }): React.ReactElement {
  return (
    <Link
      className="prow"
      to={`/shipped/releases/${encodeURIComponent(release.repositoryName)}/${release.number}?date=${release.date}`}
      style={{ textDecoration: 'none', color: 'var(--ink)', gridTemplateColumns: '96px minmax(0,1fr) auto', padding: '9px 0' }}
    >
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{release.dateLabel}</span>
      <span style={release.line === 'Housekeeping only' ? { color: 'var(--muted)' } : undefined}>{release.line}</span>
      <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
        {release.direct ? 'Straight to production' : plural(release.prs, 'PR', 'PRs')} <ArrowIcon />
      </span>
    </Link>
  );
}

function ItemRow({ item }: { readonly item: Item }): React.ReactElement {
  return (
    <SourceLink className="prow" href={item.url} style={{ textDecoration: 'none', color: 'var(--ink)', gridTemplateColumns: '96px minmax(0,1fr) auto', padding: '9px 0' }}>
      <span className="mono" style={{ fontSize: 12, color: 'var(--accent-ink)' }}>
        {item.ref}
      </span>
      <span>{item.title}</span>
      <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
        {plural(item.prs, 'PR', 'PRs')}
        {item.when === null ? '' : ` · ${item.when}`}
      </span>
    </SourceLink>
  );
}

/**
 * Writes a project's copy to the clipboard (SHP-P5) and says so for a moment. Where the browser
 * refuses the clipboard, the text appears selected in a box to copy by hand.
 */
export function CopyButton({ text }: { readonly text: string | null }): React.ReactElement {
  const [state, setState] = React.useState<'idle' | 'copied' | 'refused'>('idle');
  const box = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => {
    if (state === 'refused') box.current?.select();
  }, [state]);
  const copy = () => {
    if (text === null) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setState('copied');
        window.setTimeout(() => setState('idle'), 2000);
      })
      .catch(() => setState('refused'));
  };
  return (
    <>
      <button type="button" className="btn" onClick={copy} disabled={text === null} title={text === null ? 'Nothing reached production' : undefined} aria-live="polite">
        {state === 'copied' ? 'Copied' : 'Copy for a client update'}
      </button>
      {state === 'refused' && text !== null ? (
        <label className="stack" style={{ gap: 4, width: '100%' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>The browser didn&apos;t allow copying. The text is selected: copy it from here.</span>
          <textarea ref={box} readOnly value={text} rows={Math.min(8, text.split('\n').length)} style={{ width: '100%', font: 'inherit', fontSize: 13 }} />
        </label>
      ) : null}
    </>
  );
}

function ProjectCard({ project }: { readonly project: Project }): React.ReactElement {
  return (
    <article className="card pad stack" style={{ gap: 14 }} aria-labelledby={`ship-${project.key}`}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 id={`ship-${project.key}`}>{project.name}</h2>
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          <b className="num" style={{ color: project.toProduction > 0 ? 'var(--accent-ink)' : 'var(--warn-ink)' }}>
            {project.toProduction}
          </b>{' '}
          to production · <b className="num">{project.toDev}</b> to dev
        </span>
      </div>
      <NarrativeText narrative={project.narrative} />
      {project.releases.length > 0 ? (
        <section className="stack" style={{ gap: 0 }}>
          <span className="eyebrow" style={{ marginBottom: 4 }}>
            Delivered to production
          </span>
          <FirstFive label="releases" rows={project.releases.map((release) => <ReleaseRow key={`${release.repositoryName}#${release.number}`} release={release} />)} />
        </section>
      ) : null}
      {project.inDev.length > 0 ? (
        <section className="stack" style={{ gap: 0 }}>
          <span className="eyebrow" style={{ marginBottom: 4 }}>
            In dev, not yet in production
          </span>
          <FirstFive label="items in dev" rows={project.inDev.map((item) => <ItemRow key={item.ref} item={item} />)} />
        </section>
      ) : null}
      <div className="row" style={{ gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <CopyButton text={project.copy} />
        <Link className="linkish" to={`/projects/${project.key}`}>
          Open project <ArrowIcon />
        </Link>
      </div>
    </article>
  );
}

export function ShippedView({ data, freshness }: { readonly data: ShippedResponse; readonly freshness: Freshness | null }): React.ReactElement {
  const periodTitle = data.isCurrent ? (data.view === 'week' ? 'This week' : 'This month so far') : data.view === 'week' ? 'That week' : data.label;
  const top = Math.max(1, ...(data.weeks ?? []).map((week) => week.count));
  return (
    <>
      <Kpis items={data.tiles} />
      <section className="card pad stack" style={{ gap: 8, marginBottom: 16 }} aria-label={`The ${data.view}`}>
        <h2>
          {periodTitle} <IncompleteMark figure="shipped" freshness={freshness} />
        </h2>
        <NarrativeText narrative={data.team} />
      </section>
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 340px' }}>
        <div className="stack" style={{ gap: 16 }}>
          {data.projects.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>No project merged anything {data.view === 'week' ? 'this week' : 'this month'}.</span> : null}
          {data.projects.map((project) => (
            <ProjectCard key={project.key} project={project} />
          ))}
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          {data.weeks !== null ? (
            <section className="card pad stack" style={{ gap: 10 }}>
              <h2>To production, week by week</h2>
              <div className="mini-bars">
                {data.weeks.map((week) => (
                  <div key={week.label} className="stack" style={{ gap: 4, alignItems: 'center', flex: 1 }}>
                    <span className="num" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {week.count}
                    </span>
                    <i style={{ height: `${((week.count / top) * 90).toFixed(0)}px` }} />
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{week.label}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>How this reads</h2>
            <span style={{ color: 'var(--ink-2)' }}>
              Talos writes each finished week&apos;s narrative on Monday and each month&apos;s on the 1st, from the same lines. Until then Pulse writes a plain summary. A dev PR counts as delivered when the next release into main
              goes out. Delivered to production lists each release; open one for its update and the stories, tasks and fixes it carried.
            </span>
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>For client updates</h2>
            <span style={{ color: 'var(--ink-2)' }}>&quot;Copy for a client update&quot; copies a project&apos;s narrative and its delivered list as plain text.</span>
          </section>
        </aside>
      </div>
    </>
  );
}
